import { computed, ref } from 'vue'
import type { OptionsValuesInterface } from '@/modules/core/models/types'
import type LifeHiringAnswers from '../../hiring/models/APP/LifeHiringAnswers'

const NONE_VALUE = '998'

enum RiskCheckedStateEnum {
  NONE = 0,
  ALLOW_WITHOUT_PRICE_RISE = 1,
  ALLOW_WITH_PRICE_RISE = 2,
  DISALLOW = 3
}

export function useProfessions(answers: LifeHiringAnswers) {
  const insured = answers.insureds[0]

  const previousSavedProfessionCode = insured.profession
  const previousSavedActivityCode = insured.activity

  const professionCode = ref<string>(previousSavedProfessionCode ?? '')
  const activityCode = ref<string>(previousSavedActivityCode ?? NONE_VALUE)
  const professions = ref<OptionsValuesInterface[]>([])
  const activities = ref<OptionsValuesInterface[]>([])
  const riskChecked = ref<RiskCheckedStateEnum>(RiskCheckedStateEnum.NONE)
  const isProfessionHighRisk = ref(false)
  const isProfessionLowRisk = ref(false)
  const isActivityHighRisk = ref(false)
  const professionPriceRice = ref(false)
  const activityPriceRice = ref(false)

  const profession = computed({
    get: () => {
      const profession = professions.value.find((p) => p.value === professionCode.value)
      return profession?.label ?? ''
    },
    set: (value: string | undefined) => {
      const profession = professions.value.find(({ label }) => label === value)
      professionCode.value = profession?.value ?? ''
    }
  })

  const activity = computed(() => {
    const activity = activities.value.find((p) => p.value === activityCode.value)
    return activity?.label ?? ''
  })

  function skipRiskChecking(): boolean {
    const sameProfession = previousSavedProfessionCode === professionCode.value
    const sameActivity = previousSavedActivityCode === activityCode.value
    const inputFilled = !!previousSavedProfessionCode && !!previousSavedActivityCode

    return sameProfession && sameActivity && inputFilled
  }

  function getRiskCheckedState(allowsHiring: boolean, priceRice: boolean) {
    if (!allowsHiring) return RiskCheckedStateEnum.DISALLOW

    return priceRice
      ? RiskCheckedStateEnum.ALLOW_WITH_PRICE_RISE
      : RiskCheckedStateEnum.ALLOW_WITHOUT_PRICE_RISE
  }

  function resetAll() {
    professionCode.value = previousSavedProfessionCode ?? ''
    activityCode.value = previousSavedActivityCode ?? NONE_VALUE
    riskChecked.value = RiskCheckedStateEnum.NONE
    isProfessionHighRisk.value = false
    isProfessionLowRisk.value = false
    isActivityHighRisk.value = false
    professionPriceRice.value = false
    activityPriceRice.value = false
  }

  return {
    NONE_VALUE,
    RiskCheckedStateEnum,

    professionCode,
    activityCode,
    professions,
    activities,
    riskChecked,
    isProfessionHighRisk,
    isProfessionLowRisk,
    isActivityHighRisk,
    professionPriceRice,
    activityPriceRice,
    profession,
    activity,
    skipRiskChecking,
    resetAll,
    getRiskCheckedState
  }
}
